import { memo } from "react";
import { ProjectDetailProps } from "@/type/ProjectTypes";
import { DateUtils } from "@/utils/dateUtil";
import { cn } from "@/lib/utils";
import imgUrlMapper from "@/utils/imgUrl-mapping";
import { useQuery } from "@tanstack/react-query";
import SupabasePool from "@/lib/supabaseClient";
import LoadingSpiner from "@/components/ui/loading-spiner";
import { useNavigate } from "react-router-dom";
import HomeSectionDescription from "@/page/Home/components/home-section-description";

export type PinnedProject = {
  id: number;
  fk_project_id: number;
  created_at: string;
  project_meta: Omit<ProjectDetailProps, "project_pin">;
};

const ProjectFirstItem = () => {
  const nav = useNavigate();

  const { data } = useQuery<PinnedProject[]>({
    queryKey: ["project-pin"],
    queryFn: async () => {
      const { data, error } = await SupabasePool.getInstance().from(
        "project_pin"
      ).select(`
          *,
          project_meta(
            *,
            project_meta_stack(
              project_stack(*)
            )
          )
        `);
      if (error) throw new Error(error.message);
      return data;
    },
    staleTime: Infinity,
  });

  if (!data) return <LoadingSpiner />;
  if (data.length === 0) return null;

  return (
    <section className="grid  lg:grid-cols-[2fr_5fr]  mt-10">
      <div className="pr-15">
        <aside className="lg:sticky lg:top-40 flex flex-col items-start">
          <HomeSectionDescription className=" leading-relaxed max-w-[360px] mb-6 items-start flex flex-col">
            <h1 className=" pb-1 border-border text-zinc-200 text-2xl">
              pinned project
            </h1>
            <p className="text-sm leading-relaxed text-zinc-400">
              React·Next.js 기반으로 진행한 주요 프로젝트입니다. 최신 스택을
              사용하며 기술적 완성도를 높이는 데 집중했습니다.
            </p>
          </HomeSectionDescription>
        </aside>
      </div>

      <section className=" flex flex-col gap-10 lg:pt-0 pt-5">
        {data.map((project) => {
          const {
            title,
            thumbnail,
            description,
            start_date,
            end_date,
            id,
            project_member,
            project_meta_stack,
          } = project.project_meta;

          const stack = project_meta_stack.flatMap((e) => e.project_stack);
          const frameWork = stack.find(
            (e) =>
              e.type === "framework" &&
              (e.stack === "React" || e.stack === "Next.js")
          );

          return (
            <div
              key={id}
              onClick={() => nav(`/work/${id}`)}
              className={cn(
                "group cursor-pointer grid grid-cols-[1fr_2fr] lg:grid-cols-[2fr_2fr] gap-10 w-full rounded-xl transition-all duration-500"
              )}
            >
              <div className="lg:aspect-8/5 rounded-lg relative overflow-hidden  transition-all  grayscale-50 hover:grayscale-0 ">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.05]"
                  style={{
                    backgroundImage: `url(${imgUrlMapper({ thumbnail })})`,
                  }}
                />
              </div>
              <div className="flex flex-col gap-2 md:py-5">
                <span className="text-xs text-pink-400">
                  {frameWork?.stack}
                </span>
                <h3 className="text-2xl font-semibold tracking-tight text-zinc-50">
                  {title}
                </h3>
                <p className="text-sm text-zinc-400 my-2 leading-relaxed line-clamp-2">
                  {description}
                </p>
                <div className="lg:block hidden flex items-center gap-4 text-[0.85rem] text-zinc-400 mt-3">
                  <span>{project_member}</span>
                  <span className="opacity-40">•</span>
                  <span>
                    {DateUtils.getDurationDays(start_date!, end_date!)}일
                  </span>
                </div>
              </div>{" "}
            </div>
          );
        })}
      </section>
    </section>
  );
};

export default memo(ProjectFirstItem);
